import React, { FC, ReactNode, RefObject } from "react";

import {
    Form as RsuiteForm,
    FormInstance,
    Heading,
    HStack,
    Schema,
    SelectPicker,
    Text,
} from "rsuite";
import { ItemDataType } from "rsuite/esm/internals/types";
import { FormProps } from "rsuite/esm/Form/Form";
import { useTranslation } from "react-i18next";

import i18n from "utils/i18n";

import { User } from "store/environments/tenants/users/users-types";

import "./Form.less";

export type TenantFormValue = {
    displayName?: string;
    user?: User | null;
    link?: string | null;
};

interface Props extends FormProps {
    title?: string;

    formRef: React.Ref<FormInstance>;
    containerRef: RefObject<HTMLDivElement>;

    users: ReadonlyArray<User>;
}

const Form: FC<Props> = ({
    title = i18n.t("tenantConfig.studio.tenantForm.header"),
    formRef,
    containerRef,
    users,
    ...props
}: Props) => {
    const { t } = useTranslation();

    type formValueType = keyof TenantFormValue;

    const formFieldKeys: Record<string, formValueType> = {
        displayName: "displayName",
        user: "user",
    };

    const formValue = props.formValue as TenantFormValue;

    const { ObjectType } = Schema.Types;

    const model = Schema.Model({
        user: ObjectType().isRequired(
            t("tenantConfig.studio.validation.required")
        ),
    });

    const usersData: ItemDataType<any>[] =
        users?.map((user) => ({
            label: user.name,
            value: user,
        })) ?? [];

    const renderDropdownItemHandler = (
        label: ReactNode,
        item: ItemDataType<any>
    ) => {
        return (
            <div key={item.value.userPrincipalName}>
                <div>{label}</div>
                <div>{item.value.userPrincipalName}</div>
            </div>
        );
    };

    const searchByHandler = (
        keyword: string,
        label: ReactNode,
        item: ItemDataType<any>
    ) => {
        const query = keyword.toLowerCase();

        return (
            item.value?.userPrincipalName.includes(query) ||
            label?.toString()?.toLowerCase().includes(query)
        );
    };

    return (
        <>
            <RsuiteForm {...props} ref={formRef} model={model}>
                <Heading style={{ marginBottom: "1rem" }} level={3}>
                    {title}
                </Heading>

                {formValue.link && (
                    <HStack
                        style={{ margin: "1rem 0" }}
                        justifyContent={"flex-start"}
                        spacing={5}
                    >
                        <HStack.Item alignSelf={"center"}>
                            <Text>
                                {t("tenantConfig.studio.tenantForm.linkPart1")}
                            </Text>
                        </HStack.Item>

                        <HStack.Item alignSelf={"center"}>
                            <a
                                className={"studio__form__link"}
                                href={formValue.link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t("tenantConfig.studio.tenantForm.linkPart2")}
                            </a>
                        </HStack.Item>
                    </HStack>
                )}

                <RsuiteForm.Group
                    className={"studio__form__group"}
                    style={{ marginTop: "1rem" }}
                >
                    <RsuiteForm.ControlLabel className={"studio__form__label"}>
                        {t("tenantConfig.studio.tenantForm.displayName.label")}
                        <RsuiteForm.HelpText
                            className={"studio__form__help"}
                            tooltip
                        >
                            {t(
                                "tenantConfig.studio.tenantForm.displayName.helpText"
                            )}
                        </RsuiteForm.HelpText>
                    </RsuiteForm.ControlLabel>

                    <RsuiteForm.Control
                        className={"studio__form__control"}
                        type="text"
                        readOnly
                        name={formFieldKeys.displayName}
                    />
                </RsuiteForm.Group>

                <RsuiteForm.Group className={"studio__form__group"}>
                    <RsuiteForm.ControlLabel className={"studio__form__label"}>
                        {t("tenantConfig.studio.tenantForm.user.label")}
                        <sup className={"studio__sub"}>*</sup>
                        <RsuiteForm.HelpText
                            className={"studio__form__help"}
                            tooltip
                        >
                            {t("tenantConfig.studio.tenantForm.user.helpText")}
                        </RsuiteForm.HelpText>
                    </RsuiteForm.ControlLabel>

                    <RsuiteForm.Control
                        className={"studio__form__control"}
                        accepter={SelectPicker}
                        container={() => containerRef.current!}
                        renderMenuItem={renderDropdownItemHandler}
                        searchBy={searchByHandler}
                        data={usersData}
                        value={props.formValue?.user}
                        virtualized={false}
                        name={formFieldKeys.user}
                        placeholder={t(
                            "tenantConfig.studio.tenantForm.user.placeholder"
                        )}
                    />
                </RsuiteForm.Group>
            </RsuiteForm>
        </>
    );
};

export default Form;
